import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Snackbar, CircularProgress } from "@mui/material";
import Table from "../Table";
import AccessibleTooltip from "../Tooltip/Tooltip";
import EditIcon from "../../assets/images/editIcon.svg";
import SubHeader from "../SubHeader";
import DeleteIcon from "../../assets/images/DeleteIcon.svg";
import licence from "../../assets/images/license.svg";
import checkedLicense from "../../assets/images/checkedLicense.svg";
import AddEditBuilding from "./AddEditBuilding";
import DeleteModal from "./DeleteModal";
import MapModal from "../MapModal/MapModal";
import { jwtDecode } from "jwt-decode";
import {
  BuildingManagementData,
  deleteBuidling,
  setToastOpen,
  setToastMessage,
} from "../../Actions/BuildingManagementAction";
import LicenceModal from "./LicenceModal";
import { useDispatch, useSelector } from "react-redux";
import isAccess from "../../utils/checkAccess";
import "./mustyle.scss";
import {
  prepareRows,
  handleEditBuilding,
  handleDeleteBuilding,
  handleToastCloseUtil,
  handleLocationSelectUtil,
  getColumns,
} from "../../utils/buildingManagementUtils.js";

const BuildingManagement = () => {
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedEditId, setSelectedEditid] = useState();
  const [actionButton, setActionButton] = useState("");
  const [editData, setEditData] = useState(null);
  const [deviceIdToDelete, setDeviceIdToDelete] = useState(null);
  const [isBuildingActive, setIsBuildingActive] = useState(false);
  const [marker, setMarker] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [licenseData, setLicenseData] = useState({ license: "", customerID: "" });
  const [mode, setMode] = useState("view");
  const [hasData, setHasData] = useState(true);

  const token = JSON.parse(localStorage.getItem("token"));
  const decodedToken = jwtDecode(token);
  const { create, edit, delete: canDelete } = isAccess(decodedToken.userRole, "Building Management");

  const dispatch = useDispatch();
  const { dashboardData, loading, toastMessage, toastOpen, markerData, loadError, isLoaded } =
    useSelector((state) => state.buildingData);

  useEffect(() => {
    dispatch(BuildingManagementData());
  }, [actionButton]);

  const handleEdit = (deviceData, deviceId) =>
    handleEditBuilding(deviceData, deviceId, setSelectedEditid, setEditData, setActionButton, setOpen, dispatch);

  const handleDelete = (id) =>{

    handleDeleteBuilding(dispatch, deleteBuidling, setToastMessage, setToastOpen, id);
  }

  const handleToastClose = () =>
    handleToastCloseUtil(dispatch, setToastMessage, setToastOpen);

  const handleLocationSelect = useCallback(handleLocationSelectUtil(setMarker, setMapOpen), []);
 
  const rows = prepareRows(dashboardData);

  const columns = getColumns({
    edit,
    canDelete,
    handleEdit,
    setOpenDeleteModal,
    setDeviceIdToDelete,
    setLicenseData,
    setOpenModal,
    EditIcon,
    DeleteIcon,
    licence,
    checkedLicense,
    AccessibleTooltip,
    setIsBuildingActive,
  });

  const createUserBtnRef = useRef(null);

  useEffect(() => {
    if (createUserBtnRef.current) createUserBtnRef.current.focus();
  }, []);
  return (
    <>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {!hasData
          ? "No data available"
          : `${rows.length} item${rows.length > 1 ? "s" : ""} found`}
      </span>

      <main
        className="main_ele_wrapper userMangement_wrapper"
        role="main"
        aria-label="Building Management Section"
      >
        <SubHeader
          heading="Building Management"
          headerButton={
            create && (
              <div className="AddUser new-entry">
                <button
                  id="create-new-building-btn"
                  name="Create-New-Building"
                  className="addDevice"
                  onClick={() => {
                    setOpen(true);
                    setActionButton("add");
                  }}
                  aria-label="Add Building"
                  ref={createUserBtnRef}
                >
                  Add Building
                </button>
              </div>
            )
          }
        />

        {/*Restored new-EntryForMobile */}
        <div className="new-EntryForMobile">
          {create && (
            <div
              className="AddUser new-entry"
              data-testid="add-user-button-wrapper"
            >
              <button
                id="create-new-user-btn"
                name="Create-New-User"
                className="containedButton"
                onClick={() => {
                  setOpen(true);
                  setActionButton("add");
                }}
                aria-label="Add Building"
              >
                Add Building
              </button>
            </div>
          )}
        </div>

        <div className="inner_Wrapper">
          <div className="floorMangementTable_wrapper manageUserTable_wrapper">
            {loading && rows.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 400, width: "100%" }}>
                <Table
                  columns={columns}
                  rows={rows}
                  setHasData={setHasData}
                  pageSizeOptions={[5]}
                />
              </Box>
            )}
          </div>
        </div>
      </main>

      <AddEditBuilding
        setMapOpen={setMapOpen}
        open={open}
        setOpen={setOpen}
        actionButton={actionButton}
        editData={editData}
        setActionButton={setActionButton}
        create={create}
        edit={edit}
        setMode={setMode}
        handleClose={() => {
          setTimeout(() => {
            if (document.getElementById(selectedEditId)) {
              document.getElementById(selectedEditId).focus();
            }
          }, 200);
        }}
      />

      <DeleteModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDelete={() => handleDelete(deviceIdToDelete)}
        isBuildingActive={isBuildingActive}
        setIsBuildingActive={setIsBuildingActive}
      />

      <LicenceModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        heading="License"
        licenseData={licenseData}
      />

      <MapModal
        customClass="containerStyle"
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        center={marker}
        zoom={marker ? 15 : 4}
        setMarker={setMarker}
        actionButton={actionButton}
        markerData={markerData}
        type="modal"
        onSelectLocation={mode === "select" ? handleLocationSelect : undefined}
        loadError={loadError}
        isLoaded={isLoaded}
      />

      <Snackbar
        open={toastOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        message={toastMessage}
        onClose={handleToastClose}
        className="success"
      />
    </>
  );
};

export default BuildingManagement;
